import React from 'react';
import { FormattedMessage } from 'react-intl';

import DisplayNameFormatter from 'components/DisplayNameFormatter';
import { StyledReference } from 'components/notifications/Notification/components/Body/components/Shared/styles';
import { VISIBILITY } from 'constants/ChatConstants';
import { EXTENSIONS_ENUM } from 'constants/ExtensionConstants';
import { FEATURES, PARTS_REMINDER, REMINDER } from 'constants/FeatureConstants';
import { MODULES } from 'constants/ModuleConstants';
import { PATH } from 'constants/RoutesConstants';
import { USER_TYPE } from 'constants/UserConstants';

import { NotificationSetting } from 'modules/global/typing/enums/notifications.enum';

import * as Utils from './utils';

/**
 * @typedef {{
 *    badge?:string,
 *    flatten?: Object,
 *    icon?:string | React.ReactNode,
 *    link?: string,
 *    linkReference?: { title?: string, path?: string },
 *    title?: string,
 *    navigationState?: {
 *      scrollTo?: string,
 *    }
 * }} UINotificationSetting
 */
/**
 * @typedef {{
 *   [field: string] : {
 *     [field: string] : {
 *        disabledOptIn?: boolean,
 *        ui?: (notification:import('modules/notifications/types').Notification) => UINotificationSetting,
 *      }
 *   }
 * }} NotificationSettingDefinition
 */

/**
 * @param {{intervention: import('modules/interventions/types/projections/basic-intervention').BasicIntervention} & import('modules/notifications/types').Notification} notification
 * @returns {UINotificationSetting}
 */
const getInterventionNotificationBase = (
  { id, intervention, extension, eventType, subType },
  includeSubType = false
) => {
  const titleRoot = extension === PARTS_REMINDER ? REMINDER : extension;
  const title = includeSubType && subType ? `${titleRoot}.${eventType}_${subType}` : `${titleRoot}.${eventType}`;

  return {
    title,
    badge: MODULES[intervention.module].icon,
    link: Utils.generatePath(id, MODULES[intervention.module].path, { interventionId: intervention.id }),
    linkReference: {
      title: intervention.referenceId,
    },
  };
};

const getAppointmentNotificationBase = ({ id, appointment }) => ({
  badge: 'fb-icon--calendar',
  link: Utils.generatePath(id, PATH.KEY_DROP_OFF_APPOINTMENTS_DETAILED.path, {
    appointmentId: appointment.id,
  }),
  linkReference: {
    title: appointment.reference,
  },
});

const getAuditNotificationBase = ({ id, audit, createdBy }) => ({
  badge: 'fb-icon--ext-audits',
  link: Utils.generatePath(id, PATH.AUDITS_DETAILED.path, {
    auditId: audit.id,
  }),
  linkReference: {
    title: audit.reference,
  },
  flatten: {
    userName: createdBy?.type === USER_TYPE.SYSTEM
      ? <FormattedMessage id="common.system" defaultMessage="the system" />
      : <StyledReference><DisplayNameFormatter user={createdBy} /></StyledReference>,
  },
});

const getCustomerInfoUpdatedNotificationBase = ({ eventType, detail, extension, ...notification }) => ({
  ...getInterventionNotificationBase(notification),
  title: `${extension}.${eventType}.${detail}`,
  navigationState: {
    scrollTo: 'contact-information',
  },
});

const REMINDER_SETTINGS = {
  [NotificationSetting.ACCEPTED]: {
    ui: notification => ({
      icon: 'answered',
      ...getInterventionNotificationBase(notification),
    }),
  },
  [NotificationSetting.ASSIGNEE_CHANGED]: {
    ui: notification => ({
      icon: 'asign-to',
      ...getInterventionNotificationBase(notification),
    }),
  },
  [NotificationSetting.CREATE]: {
    disabledOptIn: true,
    ui: notification => ({
      icon: 'add',
      ...getInterventionNotificationBase(notification),
    }),
  },
  [NotificationSetting.CUSTOM_STEP_CHANGED]: {
    ui: notification => ({
      icon: 'step',
      ...getInterventionNotificationBase(notification),
      flatten: {
        stepName: <StyledReference>{notification.intervention.stepName}</StyledReference>,
      },
    }),
  },
  [NotificationSetting.DUPLICATED]: {
    ui: notification => ({
      icon: 'duplicate',
      ...getInterventionNotificationBase(notification),
    }),
  },
  [NotificationSetting.READ]: {
    ui: notification => ({
      icon: 'eye',
      ...getInterventionNotificationBase(notification),
    }),
  },
  [NotificationSetting.REJECTED]: {
    ui: notification => ({
      icon: 'no-message',
      ...getInterventionNotificationBase(notification),
    }),
  },
  [NotificationSetting.REOPENED]: {
    ui: notification => ({
      icon: 'reopened',
      ...getInterventionNotificationBase(notification),
    }),
  },
  DOCUMENT_SIGNED: {
    ui: notification => ({
      icon: 'signed',
      ...getInterventionNotificationBase(notification),
    }),
  },
};

/** @type {NotificationSettingDefinition} */
const Settings = {
  [EXTENSIONS_ENUM.AFTER_SALES]: {
    [NotificationSetting.ACCEPTED]: {
      ui: notification => ({
        icon: 'answered',
        ...getInterventionNotificationBase(notification),
      }),
    },
    [NotificationSetting.ASSIGNEE_CHANGED]: {
      ui: notification => ({
        icon: 'asign-to',
        ...getInterventionNotificationBase(notification),
      }),
    },
    [NotificationSetting.SERVICE_ADVISOR_ASSIGNED]: {
      disabledOptIn: true,
      ui: notification => ({
        icon: 'asign-to',
        ...getInterventionNotificationBase(notification),
      }),
    },
    [NotificationSetting.CREATE]: {
      disabledOptIn: true,
      ui: notification => ({
        icon: 'add',
        ...getInterventionNotificationBase(notification),
      }),
    },
    [NotificationSetting.CUSTOM_STEP_CHANGED]: {
      ui: notification => ({
        icon: 'step',
        ...getInterventionNotificationBase(notification),
        flatten: {
          stepName: notification.intervention.stepName,
        },
      }),
    },
    [NotificationSetting.DUPLICATED]: {
      ui: notification => ({
        icon: 'duplicate',
        ...getInterventionNotificationBase(notification),
      }),
    },
    [NotificationSetting.PAYMENT_DEPOSIT_SUCCESS]: {
      ui: notification => ({
        icon: 'credit-card',
        ...getInterventionNotificationBase(notification),
      }),
    },
    [NotificationSetting.PAYMENT_SUCCESS]: {
      ui: notification => ({
        icon: 'credit-card',
        ...getInterventionNotificationBase(notification, true),
      }),
    },
    [NotificationSetting.RATED]: {
      ui: notification => ({
        icon: 'badRating',
        ...getInterventionNotificationBase(notification),
      }),
    },
    [NotificationSetting.READ]: {
      ui: notification => ({
        icon: 'eye',
        ...getInterventionNotificationBase(notification),
      }),
    },
    [NotificationSetting.REJECTED]: {
      ui: notification => ({
        icon: 'no-message',
        ...getInterventionNotificationBase(notification),
      }),
    },
    [NotificationSetting.REOPENED]: {
      ui: notification => ({
        icon: 'reopened',
        ...getInterventionNotificationBase(notification),
      }),
    },
    DOCUMENT_SIGNED: {
      ui: notification => ({
        icon: 'signed',
        ...getInterventionNotificationBase(notification),
      }),
    },
  },
  [EXTENSIONS_ENUM.APPOINTMENT]: {
    CREATE: {
      ui: ({ id, appointment }) => ({
        icon: 'add',
        ...getAppointmentNotificationBase({ id, appointment }),
      }),
    },
    DOCUMENT_ACCEPTED: {
      ui: ({ id, appointment }) => ({
        icon: 'new-file',
        ...getAppointmentNotificationBase({ id, appointment }),
      }),
    },
    DOCUMENT_REFUSED: {
      ui: ({ id, appointment }) => ({
        icon: 'new-file',
        ...getAppointmentNotificationBase({ id, appointment }),
      }),
    },
    DROP_OFF_LOCKER_OPENED: {
      ui: ({ id, appointment }) => ({
        icon: 'vault-deposit',
        ...getAppointmentNotificationBase({ id, appointment }),
      }),
    },
    DROP_OFF_LOCKER_PREPARED: {
      ui: ({ id, appointment }) => ({
        icon: 'vault-deposit',
        ...getAppointmentNotificationBase({ id, appointment }),
      }),
    },
    DROP_OFF_LOCKER_VERIFIED: {
      ui: ({ id, appointment }) => ({
        icon: 'vault-deposit',
        ...getAppointmentNotificationBase({ id, appointment }),
      }),
    },
    DROP_OFF_LOCKER_VERIFIED_ISSUE: {
      ui: ({ id, appointment }) => ({
        icon: 'vault-deposit',
        ...getAppointmentNotificationBase({ id, appointment }),
      }),
    },
    DROP_OFF_CONFIRMED: {
      ui: ({ id, appointment }) => ({
        icon: 'vault-deposit',
        ...getAppointmentNotificationBase({ id, appointment }),
      }),
    },
    ISSUE_REPORTED: {
      ui: ({ id, appointment }) => ({
        icon: 'issue',
        ...getAppointmentNotificationBase({ id, appointment }),
      }),
    },
    PAYMENT_SUCCESS: {
      ui: ({ id, appointment }) => ({
        icon: 'credit-card',
        ...getAppointmentNotificationBase({ id, appointment }),
      }),
    },
    PICK_UP_LOCKER_OPENED: {
      ui: ({ id, appointment }) => ({
        icon: 'vault-deposit',
        ...getAppointmentNotificationBase({ id, appointment }),
      }),
    },
    PICK_UP_LOCKER_PREPARED: {
      ui: ({ id, appointment }) => ({
        icon: 'vault-deposit',
        ...getAppointmentNotificationBase({ id, appointment }),
      }),
    },
    PICK_UP_LOCKER_VERIFIED: {
      ui: ({ id, appointment }) => ({
        icon: 'vault-deposit',
        ...getAppointmentNotificationBase({ id, appointment }),
      }),
    },
    PICK_UP_LOCKER_VERIFIED_ISSUE: {
      ui: ({ id, appointment }) => ({
        icon: 'vault-deposit',
        ...getAppointmentNotificationBase({ id, appointment }),
      }),
    },
    PICK_UP_CONFIRMED: {
      ui: ({ id, appointment }) => ({
        icon: 'vault-deposit',
        ...getAppointmentNotificationBase({ id, appointment }),
      }),
    },
    RENTAL_DOCUMENTS_SIGNED: {
      ui: ({ id, appointment }) => ({
        icon: 'signed',
        ...getAppointmentNotificationBase({ id, appointment }),
      }),
    },
    REOPENED: {
      ui: ({ id, appointment }) => ({
        icon: 'reopened',
        ...getAppointmentNotificationBase({ id, appointment }),
      }),
    },
    REPAIR_ORDER_ANSWERED: {
      ui: ({ id, appointment }) => ({
        icon: 'answered',
        ...getAppointmentNotificationBase({ id, appointment }),
      }),
    },
  },
  [EXTENSIONS_ENUM.AUDIT]: {
    ASSIGNEE_CHANGED: {
      ui: notification => ({
        icon: 'asign-to',
        ...getAuditNotificationBase(notification),
      }),
    },
    COMPLETED: {
      ui: notification => ({
        icon: 'check',
        ...getAuditNotificationBase(notification),
      }),
    },
    CREATED: {
      disabledOptIn: true,
      ui: notification => ({
        icon: 'add',
        ...getAuditNotificationBase(notification),
      }),
    },
    STARTED: {
      ui: notification => ({
        icon: 'check',
        ...getAuditNotificationBase(notification),
      }),
    },
    DUPLICATED: {
      ui: notification => ({
        icon: 'duplicate',
        ...getAuditNotificationBase(notification),
      }),
    },
    KPI_ANSWERED: {
      ui: notification => ({
        icon: 'answered',
        ...getAuditNotificationBase(notification),
      }),
    },
    REOPENED: {
      ui: notification => ({
        icon: 'reopened',
        ...getAuditNotificationBase(notification),
      }),
    },
  },
  [FEATURES.BODYWORK_APPOINTMENT]: {
    CREATE: {
      disabledOptIn: true,
      ui: notification => ({
        icon: 'add',
        ...getInterventionNotificationBase(notification),
      }),
    },
    CUSTOM_STEP_CHANGED: {
      ui: notification => ({
        icon: 'step',
        ...getInterventionNotificationBase(notification),
        flatten: {
          stepName: <StyledReference>{notification.intervention.stepName}</StyledReference>,
        },
      }),
    },
    DUPLICATED: {
      ui: notification => ({
        icon: 'duplicate',
        ...getInterventionNotificationBase(notification),
      }),
    },
    REOPENED: {
      ui: notification => ({
        icon: 'reopened',
        ...getInterventionNotificationBase(notification),
      }),
    },
    DOCUMENT_SIGNED: {
      ui: notification => ({
        icon: 'signed',
        ...getInterventionNotificationBase(notification),
      }),
    },
  },
  [EXTENSIONS_ENUM.CHAT]: {
    CREATED: {
      disabledOptIn: true,
      ui: ({ id, conversation }) => ({
        icon: 'new-conversation',
        badge: 'fb-icon--ext-chat',
        link: Utils.generatePath(id, PATH.CHAT.path, { conversationId: conversation?.id }),
      }),
    },
    MESSAGE_CREATED: {
      /**
       * @param {import('modules/notifications/types').ConversationNotification} notification
       * @returns {UINotificationSetting}
       */
      ui: notification => {
        const { id, conversation, createdBy, eventType, extension } = notification;
        const { customer, latestMessage } = conversation;
        // @ts-ignore
        const isFromCustomer = createdBy?.customer ?? false;
        const messageAuthor = isFromCustomer ? customer?.displayName : createdBy?.displayName;

        const isPrivate = latestMessage.visibility === VISIBILITY.PRIVATE;
        const icon = isPrivate ? 'private-conversation' : 'new-message';

        const uiSettings = {
          icon,
          badge: 'fb-icon--ext-chat',
          title: `${extension}.${eventType}`,
          link: Utils.generatePath(id, PATH.CHAT.path, { conversationId: conversation?.id }),
          linkReference: {
            title: messageAuthor,
          },
        };

        return Utils.overrideSettingForLinkedConversation(notification, uiSettings);
      },
    },
  },
  [FEATURES.REMINDER]: REMINDER_SETTINGS,
  [EXTENSIONS_ENUM.SALES]: {
    ACCEPTED: {
      ui: notification => ({
        icon: 'answered',
        ...getInterventionNotificationBase(notification),
      }),
    },
    ANSWERED: {
      ui: notification => ({
        icon: 'answered',
        ...getInterventionNotificationBase(notification),
      }),
    },
    ASSIGNEE_CHANGED: {
      ui: notification => ({
        icon: 'asign-to',
        ...getInterventionNotificationBase(notification),
      }),
    },
    CREATE: {
      disabledOptIn: true,
      ui: notification => ({
        icon: 'add',
        ...getInterventionNotificationBase(notification),
      }),
    },
    CUSTOM_STEP_CHANGED: {
      ui: notification => ({
        icon: 'step',
        ...getInterventionNotificationBase(notification),
        flatten: {
          stepName: <StyledReference>{notification.intervention.stepName}</StyledReference>,
        },
      }),
    },
    DUPLICATED: {
      ui: notification => ({
        icon: 'duplicate',
        ...getInterventionNotificationBase(notification),
      }),
    },
    PAYMENT_DEPOSIT_SUCCESS: {
      ui: notification => ({
        icon: 'credit-card',
        ...getInterventionNotificationBase(notification),
      }),
    },
    PAYMENT_SUCCESS: {
      ui: notification => ({
        icon: 'credit-card',
        ...getInterventionNotificationBase(notification),
      }),
    },
    RATED: {
      ui: notification => ({
        icon: 'badRating',
        ...getInterventionNotificationBase(notification),
      }),
    },
    READ: {
      ui: notification => ({
        icon: 'eye',
        ...getInterventionNotificationBase(notification),
      }),
    },
    REJECTED: {
      ui: notification => ({
        icon: 'no-message',
        ...getInterventionNotificationBase(notification),
      }),
    },
    REOPENED: {
      ui: notification => ({
        icon: 'reopened',
        ...getInterventionNotificationBase(notification),
      }),
    },
    LEAD_CREATED: {
      ui: notification => ({
        icon: 'add',
        ...getInterventionNotificationBase(notification),
      }),
    },
    DOCUMENT_SIGNED: {
      ui: notification => ({
        icon: 'signed',
        ...getInterventionNotificationBase(notification),
      }),
    },
  },
  [EXTENSIONS_ENUM.PARTS]: {
    ACCEPTED: {
      ui: notification => ({
        icon: 'answered',
        ...getInterventionNotificationBase(notification),
      }),
    },
    ANSWERED: {
      ui: notification => ({
        icon: 'answered',
        ...getInterventionNotificationBase(notification),
      }),
    },
    ASSIGNEE_CHANGED: {
      ui: notification => ({
        icon: 'asign-to',
        ...getInterventionNotificationBase(notification),
      }),
    },
    CREATE: {
      disabledOptIn: true,
      ui: notification => ({
        icon: 'add',
        ...getInterventionNotificationBase(notification),
      }),
    },
    CUSTOM_STEP_CHANGED: {
      ui: notification => ({
        icon: 'step',
        ...getInterventionNotificationBase(notification),
        flatten: {
          stepName: <StyledReference>{notification.intervention.stepName}</StyledReference>,
        },
      }),
    },
    DUPLICATED: {
      ui: notification => ({
        icon: 'duplicate',
        ...getInterventionNotificationBase(notification),
      }),
    },
    PAYMENT_DEPOSIT_SUCCESS: {
      ui: notification => ({
        icon: 'credit-card',
        ...getInterventionNotificationBase(notification),
      }),
    },
    PAYMENT_SUCCESS: {
      ui: notification => ({
        icon: 'credit-card',
        ...getInterventionNotificationBase(notification),
      }),
    },
    READ: {
      ui: notification => ({
        icon: 'eye',
        ...getInterventionNotificationBase(notification),
      }),
    },
    REJECTED: {
      ui: notification => ({
        icon: 'no-message',
        ...getInterventionNotificationBase(notification),
      }),
    },
    REOPENED: {
      ui: notification => ({
        icon: 'reopened',
        ...getInterventionNotificationBase(notification),
      }),
    },
    DOCUMENT_SIGNED: {
      ui: notification => ({
        icon: 'signed',
        ...getInterventionNotificationBase(notification),
      }),
    },
  },
  [EXTENSIONS_ENUM.SELF_SERVICE]: {
    [NotificationSetting.MOBILITY_DOCUMENT_MEDIA_FRAUD_DETECTED]: {
      ui: notification => ({
        icon: 'self-service',
        ...getInterventionNotificationBase(notification),
      }),
    },
    [NotificationSetting.CUSTOMER_INFO_UPDATED]: {
      ui: notification => ({
        icon: 'self-service',
        ...getCustomerInfoUpdatedNotificationBase(notification),
      }),
    },
    [NotificationSetting.MOBILITY_DOCUMENT_MEDIA_ADDED]: {
      ui: notification => ({
        icon: 'new-file',
        ...getInterventionNotificationBase(notification),
      }),
    },
    [NotificationSetting.MOBILITY_REFUSED]: {
      ui: notification => ({
        icon: 'mobility-refused',
        ...getInterventionNotificationBase(notification),
      }),
    },
    [NotificationSetting.SELF_SERVICE_HOME_CHECK_IN_COMPLETED]: {
      ui: notification => ({
        icon: 'signed',
        ...getInterventionNotificationBase(notification),
      }),
    },
    [NotificationSetting.SELF_SERVICE_ON_SITE_CHECK_IN_COMPLETED]: {
      ui: notification => ({
        icon: 'signed',
        ...getInterventionNotificationBase(notification),
      }),
    },
    [NotificationSetting.SELF_SERVICE_KIOSK_CHECK_IN_COMPLETED]: {
      ui: notification => ({
        icon: 'signed',
        ...getInterventionNotificationBase(notification),
      }),
    },
    [NotificationSetting.SELF_SERVICE_HOME_CHECK_OUT_COMPLETED]: {
      ui: notification => ({
        icon: 'signed',
        ...getInterventionNotificationBase(notification),
      }),
    },
    [NotificationSetting.SELF_SERVICE_ON_SITE_CHECK_OUT_COMPLETED]: {
      ui: notification => ({
        icon: 'signed',
        ...getInterventionNotificationBase(notification),
      }),
    },
    [NotificationSetting.SELF_SERVICE_KIOSK_CHECK_OUT_COMPLETED]: {
      ui: notification => ({
        icon: 'signed',
        ...getInterventionNotificationBase(notification),
      }),
    },
    [NotificationSetting.SELF_SERVICE_ANSWERED_MANUAL_CHECK_OUT]: {
      ui: notification => ({
        icon: 'signed',
        ...getInterventionNotificationBase(notification),
      }),
    },
    [NotificationSetting.SELF_SERVICE_BREAKDOWN_CREATED]: {
      disabledOptIn: true,
      ui: notification => ({
        icon: 'signed',
        ...getInterventionNotificationBase(notification),
      }),
    },
    [NotificationSetting.SELF_SERVICE_COMMENT_ADDED]: {
      ui: notification => ({
        icon: 'note',
        ...getInterventionNotificationBase(notification),
      }),
    },
    [NotificationSetting.SELF_SERVICE_SLOT_EMPTIED]: {
      ui: notification => ({
        icon: 'hand-check-in',
        ...getInterventionNotificationBase(notification),
      }),
    },
    [NotificationSetting.SELF_SERVICE_SLOT_FILLED]: {
      ui: notification => ({
        icon: 'hand-check-in',
        ...getInterventionNotificationBase(notification),
      }),
    },
    [NotificationSetting.SELF_SERVICE_EXTRAS_ACCEPTED]: {
      ui: notification => ({
        icon: 'basket',
        ...getInterventionNotificationBase(notification),
      }),
    },
    [NotificationSetting.SELF_SERVICE_PROACTIVES_ACCEPTED]: {
      ui: notification => ({
        icon: 'basket',
        ...getInterventionNotificationBase(notification),
      }),
    },
    [NotificationSetting.SELF_SERVICE_QUESTION_ANSWERED]: {
      disabledOptIn: true,
      ui: notification => ({
        icon: 'self-service',
        ...getInterventionNotificationBase(notification),
      }),
    },
    [NotificationSetting.SELF_SERVICE_QUESTION_COMMENTED]: {
      ui: notification => ({
        icon: 'self-service',
        ...getInterventionNotificationBase(notification),
      }),
    },
  },
  [FEATURES.PARTS_REMINDER]: REMINDER_SETTINGS,
};

export const SETTING_ORDERS = {
  [EXTENSIONS_ENUM.AFTER_SALES]: [
    NotificationSetting.CREATED,
    NotificationSetting.READ,
    NotificationSetting.ANSWERED,
    NotificationSetting.REOPENED,
    NotificationSetting.DUPLICATED,
    NotificationSetting.STEP,
    NotificationSetting.ASSIGNED,
    NotificationSetting.SERVICE_ADVISOR_ASSIGNED,
    NotificationSetting.PAYMENT,
    NotificationSetting.RATED,
    NotificationSetting.DOCUMENT_SIGNED
  ],
  [FEATURES.REMINDER]: [
    NotificationSetting.CREATED,
    NotificationSetting.READ,
    NotificationSetting.ANSWERED,
    NotificationSetting.REOPENED,
    NotificationSetting.DUPLICATED,
    NotificationSetting.STEP,
    NotificationSetting.ASSIGNED,
    NotificationSetting.DOCUMENT_SIGNED
  ],
  [FEATURES.BODYWORK_APPOINTMENT]: [
    NotificationSetting.CREATED,
    NotificationSetting.REOPENED,
    NotificationSetting.DUPLICATED,
    NotificationSetting.STEP,
    NotificationSetting.DOCUMENT_SIGNED
  ],
  [EXTENSIONS_ENUM.SELF_SERVICE]: [
    NotificationSetting.CUSTOMER_INFO_UPDATED,
    NotificationSetting.MOBILITY_DOCUMENT_MEDIA_ADDED,
    NotificationSetting.MOBILITY_DOCUMENT_MEDIA_FRAUD_DETECTED,
    NotificationSetting.MOBILITY_REFUSED,
    NotificationSetting.SELF_SERVICE_HOME_CHECK_IN_COMPLETED,
    NotificationSetting.SELF_SERVICE_ON_SITE_CHECK_IN_COMPLETED,
    NotificationSetting.SELF_SERVICE_KIOSK_CHECK_IN_COMPLETED,
    NotificationSetting.SELF_SERVICE_HOME_CHECK_OUT_COMPLETED,
    NotificationSetting.SELF_SERVICE_ON_SITE_CHECK_OUT_COMPLETED,
    NotificationSetting.SELF_SERVICE_KIOSK_CHECK_OUT_COMPLETED,
    NotificationSetting.SELF_SERVICE_MANUAL_CHECK_OUT_COMPLETED,
    NotificationSetting.SELF_SERVICE_BREAKDOWN_CREATED,
    NotificationSetting.SELF_SERVICE_COMMENT_ADDED,
    NotificationSetting.SELF_SERVICE_EXTRAS_ACCEPTED,
    NotificationSetting.SELF_SERVICE_QUESTION_ANSWERED,
    NotificationSetting.SELF_SERVICE_QUESTION_COMMENTED,
    NotificationSetting.SELF_SERVICE_SLOT_EMPTIED,
    NotificationSetting.SELF_SERVICE_SLOT_FILLED,
    NotificationSetting.SELF_SERVICE_PROACTIVES_ACCEPTED,
  ],
  [EXTENSIONS_ENUM.PARTS]: [
    NotificationSetting.CREATED,
    NotificationSetting.READ,
    NotificationSetting.ANSWERED,
    NotificationSetting.REOPENED,
    NotificationSetting.DUPLICATED,
    NotificationSetting.STEP,
    NotificationSetting.ASSIGNED,
    NotificationSetting.PAYMENT,
    NotificationSetting.DOCUMENT_SIGNED
  ],
  [FEATURES.PARTS_REMINDER]: [
    NotificationSetting.CREATED,
    NotificationSetting.READ,
    NotificationSetting.ANSWERED,
    NotificationSetting.REOPENED,
    NotificationSetting.DUPLICATED,
    NotificationSetting.STEP,
    NotificationSetting.ASSIGNED,
    NotificationSetting.DOCUMENT_SIGNED
  ],
  [EXTENSIONS_ENUM.SALES]: [
    NotificationSetting.CREATED,
    NotificationSetting.READ,
    NotificationSetting.ANSWERED,
    NotificationSetting.REOPENED,
    NotificationSetting.DUPLICATED,
    NotificationSetting.STEP,
    NotificationSetting.ASSIGNED,
    NotificationSetting.PAYMENT,
    NotificationSetting.LEAD_CREATED,
    NotificationSetting.RATED,
    NotificationSetting.DOCUMENT_SIGNED
  ],
  [EXTENSIONS_ENUM.AUDIT]: [
    NotificationSetting.CREATED,
    NotificationSetting.STARTED,
    NotificationSetting.ASSIGNED,
    NotificationSetting.COMPLETED,
    NotificationSetting.REOPENED,
    NotificationSetting.DUPLICATED,
    NotificationSetting.KPI_ANSWERED,
  ],
  [EXTENSIONS_ENUM.CHAT]: [
    NotificationSetting.CREATED,
    NotificationSetting.MESSAGE_CREATED
  ],
  [EXTENSIONS_ENUM.APPOINTMENT]: [
    NotificationSetting.CREATED,
    NotificationSetting.ANSWERED,
    NotificationSetting.REOPENED,
    NotificationSetting.ISSUE_REPORTED,
    NotificationSetting.VAULT_UPDATED,
    NotificationSetting.DOCUMENT_UPDATED,
    NotificationSetting.PAID,
    NotificationSetting.RENTAL_SIGNED,
  ],
  [EXTENSIONS_ENUM.CAMPAIGN]: [
    NotificationSetting.ANSWERED,
    NotificationSetting.UNSUBSCRIBE,
  ]
};

const EVENT_FIELD_OVERRIDE = {
  SELF_SERVICE_ANSWERED_HOME_CHECK_IN: 'SELF_SERVICE_HOME_CHECK_IN_COMPLETED',
  SELF_SERVICE_ANSWERED_HOME_CHECK_OUT: 'SELF_SERVICE_HOME_CHECK_OUT_COMPLETED',
  SELF_SERVICE_ANSWERED_ON_SITE_CHECK_IN: 'SELF_SERVICE_ON_SITE_CHECK_IN_COMPLETED',
  SELF_SERVICE_ANSWERED_ON_SITE_CHECK_OUT: 'SELF_SERVICE_ON_SITE_CHECK_OUT_COMPLETED',
  SELF_SERVICE_ANSWERED_KIOSK_CHECK_IN: 'SELF_SERVICE_KIOSK_CHECK_IN_COMPLETED',
  SELF_SERVICE_ANSWERED_KIOSK_CHECK_OUT: 'SELF_SERVICE_KIOSK_CHECK_OUT_COMPLETED',
  SELF_SERVICE_ANSWER_REMOVED_MANUAL_CHECK_OUT: 'SELF_SERVICE_MANUAL_CHECK_OUT_REMOVED',
  SELF_SERVICE_ANSWERED_MANUAL_CHECK_OUT: 'SELF_SERVICE_ANSWERED_MANUAL_CHECK_OUT'
};

export const getEventField = ({ eventType, subType }) => EVENT_FIELD_OVERRIDE[`${eventType}_${subType}`] ?? eventType;

export default Settings;
